import React from "react";
import {
  Box,
  Button,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Typography,
  Alert, 
  CircularProgress, 
} from "@mui/material";

import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import UpdateOutlinedIcon from "@mui/icons-material/UpdateOutlined";

import type { DashboardProps } from "./dashboard.types";
import DashboardSwitcher from "./DashboardSwitcher";
import {
  dashboardRegistry,
  defaultDashboardKey,
  getDashboardByKey,
} from "./dashboardRegistry";
import FilterContextBar from "./FilterContextBar";

type Props = DashboardProps & {
  activeDashboardKey: string;
  pendingDashboardKey?: string;
  loading?: boolean;
  error?: string | null;
  onDashboardChange?: (key: string) => void;
};

type State = {
  autoRefreshEnabled: boolean;
  lastRefresh: Date | null;
};

export default class DashboardHost extends React.Component<Props, State> {
  private refreshTimer: any = null;

  state: State = {
    autoRefreshEnabled: true,
    lastRefresh: null,
  };

  componentDidMount() {
    this.setupAutoRefresh();
  }

  componentDidUpdate(prevProps: Props, prevState: State) {
    const prevKey = prevProps.activeDashboardKey;
    const currKey = this.getActiveKey();

    const dashboardChanged = prevKey !== currKey;
    const autoChanged =
      prevState.autoRefreshEnabled !== this.state.autoRefreshEnabled;

    if (dashboardChanged || autoChanged) {
      this.clearAutoRefresh();
      this.setupAutoRefresh();

      if (dashboardChanged) {
        this.refreshDashboard(currKey);
      }
    }
  }

  componentWillUnmount() {
    this.clearAutoRefresh();
  }

  getActiveKey = () => {
    return this.props.activeDashboardKey || defaultDashboardKey;
  };

  getActiveDashboard = () => {
    return getDashboardByKey(this.getActiveKey());
  };

  clearAutoRefresh = () => {
    if (this.refreshTimer) {
      clearInterval(this.refreshTimer);
      this.refreshTimer = null;
    }
  };

  setupAutoRefresh = () => {
    const activeDashboard = this.getActiveDashboard();

    if (
      !this.state.autoRefreshEnabled ||
      activeDashboard.refreshMode !== "auto"
    ) {
      return;
    }

    const intervalMs = (activeDashboard.refreshIntervalSec || 60) * 1000;

    this.refreshTimer = setInterval(() => {
      this.refreshDashboard(this.getActiveKey());
    }, intervalMs);
  };

  refreshDashboard = async (key?: string) => {
    const dashboardKey = key || this.getActiveKey();

    if (this.props.onRefresh) {
      await this.props.onRefresh(dashboardKey);
    }

    this.setState({ lastRefresh: new Date() });
  };

  handleDashboardChange = (key: string) => {
    if (this.props.onDashboardChange) {
      this.props.onDashboardChange(key);
    }
  };

  handleManualRefresh = () => {
    this.refreshDashboard(this.getActiveKey());
  };

  render() {
    const {
      userData,
      dashData,
      loading,
      error,
      activeDashboardKey,
      pendingDashboardKey,
      sharedFilters,
      filterOptions,
      onFilterChange,
      onExport,
      navigateToDrill,
    } = this.props;

    const { autoRefreshEnabled, lastRefresh } = this.state;

    const activeDashboard = this.getActiveDashboard();
    const ActiveComponent = activeDashboard.component;

    return (
      <Box sx={{ display: "grid", gap: 2 }}>
        <Box
          sx={{
            px: { xs: 2, md: 3 },
            pt: { xs: 2, md: 2.5 },
            pb: { xs: 1.5, md: 2 },
            borderBottom: "1px solid #E2E8EE",
            bgcolor: "#FFFFFF",
          }}
        >
          <Stack
            direction={{ xs: "column", lg: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "flex-start", lg: "center" }}
            spacing={2}
          >
            <Box>
              <Typography
                sx={{
                  fontSize: { xs: 26, md: 30 },
                  fontWeight: 800,
                  color: "#17324D",
                  letterSpacing: "-0.02em",
                }}
              >
                Dashboard Center
              </Typography>

              <Typography sx={{ mt: 0.5, fontSize: 14, color: "#66788A" }}>
                Tableau de bord unifié avec modes auto-refresh et navigation modernisée.
              </Typography>
            </Box>

            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={1.25}
              alignItems={{ xs: "stretch", sm: "center" }}
            >
              <TextField
                select
                size="small"
                label="Mode"
                value={activeDashboard.refreshMode}
                sx={{ minWidth: 150 }}
                disabled
              >
                <MenuItem value="manual">Manual</MenuItem>
                <MenuItem value="auto">Auto</MenuItem>
              </TextField>

              <Stack direction="row" spacing={1} alignItems="center">
                <Typography sx={{ fontSize: 13, color: "#66788A" }}>
                  Auto-refresh
                </Typography>
                <Switch
                  checked={autoRefreshEnabled}
                  onChange={(e) =>
                    this.setState({ autoRefreshEnabled: e.target.checked })
                  }
                  disabled={activeDashboard.refreshMode !== "auto"}
                />
              </Stack>

              <Button
                variant="outlined"
                startIcon={<RefreshOutlinedIcon />}
                onClick={this.handleManualRefresh}
              >
                Refresh
              </Button>
            </Stack>
          </Stack>

          <DashboardSwitcher
            items={dashboardRegistry}
            activeKey={pendingDashboardKey || activeDashboardKey}
            onChange={this.handleDashboardChange}
          />

          <FilterContextBar
            meta={dashData?.meta}
            onClear={(key) => {
              if (key === "costcenter") onFilterChange({ costcenter: null });
              if (key === "location") onFilterChange({ location: null });
              if (key === "cycle") onFilterChange({ cycle: null });
            }}
          />

          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={1.5}
            justifyContent="space-between"
            alignItems={{ xs: "flex-start", md: "center" }}
            sx={{ mt: 1.5 }}
          >
            <Typography sx={{ fontSize: 13, color: "#66788A" }}>
              Active view: <strong>{activeDashboard.title}</strong>
            </Typography>

            <Stack direction="row" spacing={1} alignItems="center">
              <UpdateOutlinedIcon sx={{ fontSize: 16, color: "#66788A" }} />
              <Typography sx={{ fontSize: 12, color: "#66788A" }}>
                {lastRefresh
                  ? `Last refresh: ${lastRefresh.toLocaleTimeString()}`
                  : "No refresh yet"}
              </Typography>
            </Stack>
          </Stack>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mx: { xs: 2, md: 3 } }}>
            {error}
          </Alert>
        )}

        <Box sx={{ minHeight: 480, position: "relative" }}>
          {loading && (
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                zIndex: 10,
                bgcolor: "rgba(255,255,255,0.72)",
                backdropFilter: "blur(2px)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                borderRadius: 2,
              }}
            >
              <Stack spacing={1.5} alignItems="center">
                <CircularProgress size={32} />
                <Typography sx={{ fontSize: 13, color: "#66788A" }}>
                  Loading dashboard...
                </Typography>
              </Stack>
            </Box>
          )}

          <ActiveComponent
            userData={userData}
            dashData={dashData}
            sharedFilters={sharedFilters}
            filterOptions={filterOptions}
            onFilterChange={onFilterChange}
            onRefresh={() => this.refreshDashboard(activeDashboard.key)}
            onExport={() => onExport?.(activeDashboard.key)}
            navigateToDrill={navigateToDrill}
          />
        </Box>

        {/* <Box sx={{ minHeight: 480 }}>
          <ActiveComponent
            userData={userData}
            dashData={dashData}
            sharedFilters={sharedFilters}
            filterOptions={filterOptions}
            onFilterChange={onFilterChange}
            onRefresh={() => this.refreshDashboard(activeDashboard.key)}
            onExport={() => onExport?.(activeDashboard.key)}
            navigateToDrill={navigateToDrill}
          />
        </Box> */}
      </Box>
    );
  }
}